/**
 * Compaction Divider component for chat conversation threads.
 *
 * Renders a horizontal "Conversation compacted" marker between messages,
 * indicating the moment when older history was replaced by an LLM-generated
 * summary to keep the prompt within the model's context window. Visually it
 * mimics a chat-app day separator (rule + centered pill) so it reads as a
 * neutral timeline boundary rather than a message.
 *
 * When a `summary` is provided the pill becomes expandable — clicking it
 * reveals the exact summary text the model now sees in place of the folded
 * turns, so compaction is transparent rather than a black box.
 *
 * @module components/chat/messages/compaction-divider
 */
import { ChevronDown, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import { cn } from "@/lib/utils";

/**
 * Props for the CompactionDivider component.
 */
export interface CompactionDividerProps {
  /** How many messages were folded into this summary. */
  messageCount: number;
  /** ISO timestamp at the moment the summary was produced. */
  createdAt: string;
  /**
   * The summary text the model now sees in place of the folded turns. When
   * present, the divider pill becomes an expandable disclosure.
   */
  summary?: string;
  /** Optional CSS class for layout overrides. */
  className?: string;
}

/**
 * CompactionDivider — a neutral timeline boundary. Hover reveals the absolute
 * timestamp via `title`; the pill shows a relative time so long-running chats
 * stay readable ("3 hours ago" vs. "2026-05-04 14:32"). When a `summary` is
 * supplied the pill expands to show what was compacted.
 */
export function CompactionDivider({
  messageCount,
  createdAt,
  summary,
  className,
}: CompactionDividerProps) {
  const [expanded, setExpanded] = useState(false);

  const { relative, absolute } = useMemo(() => {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
      return { relative: "", absolute: createdAt };
    }
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: date.toLocaleString(),
    };
  }, [createdAt]);

  const messageLabel = `${messageCount} message${messageCount === 1 ? "" : "s"}`;
  const pillText = `Conversation compacted · ${messageLabel}${
    relative ? ` · ${relative}` : ""
  }`;
  const expandable = !!summary?.trim();

  return (
    <div
      className={cn(
        "mx-auto my-4 w-full max-w-3xl px-3 lg:px-4",
        className,
      )}
      role="note"
      aria-label={`Conversation compacted ${relative} (${messageLabel})`}
    >
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        {expandable ? (
          <button
            type="button"
            title={absolute}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted",
              "px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors",
              "hover:bg-muted/70 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            )}
          >
            <ScrollText className="size-3 shrink-0" />
            <span>{pillText}</span>
            <ChevronDown
              className={cn(
                "size-3 shrink-0 transition-transform",
                expanded && "rotate-180",
              )}
            />
          </button>
        ) : (
          <span
            title={absolute}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
          >
            <ScrollText className="size-3 shrink-0" />
            <span>{pillText}</span>
          </span>
        )}
        <div className="h-px flex-1 bg-border" />
      </div>

      {expandable && expanded && (
        <div className="mx-auto mt-2 max-w-2xl rounded-lg border border-border bg-muted/40 px-3 py-2.5 animate-in fade-in slide-in-from-top-1">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Summary of earlier conversation
          </p>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
            {summary}
          </p>
        </div>
      )}
    </div>
  );
}
